"use client";

import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerMain,
	DrawerRoot,
	DrawerViewport,
	ModalContent,
	ModalFooter,
	ModalMain,
	ModalRoot,
	ModalViewport,
} from "@/components/ui/Drawer";
import { DialogClose, Slot } from "@frend-digital/ui";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { type ComponentProps, Suspense, useState, useTransition } from "react";
import { CountryForm } from "./CountryForm";
import styles from "./GeoSelector.module.css";
import { LanguageForm } from "./LanguageForm";
import { useGeo } from "@/i18n/routing";
import type { GeoLocale } from "@frend-digital/next/i18n";

export type GeoSelectorProps = {};

const useGeoSelectorModal = () => {
	const searchParams = useUrlSearchParams();

	return {
		open: searchParams.has("select-geo", "open"),
		setOpen: (open: boolean) => {
			if (open) {
				searchParams.set("select-geo", "open");
			} else {
				searchParams.delete("select-geo");
			}
		},
	};
};

export const GeoSelectorModal = ({}: GeoSelectorProps) => {
	const { open, setOpen } = useGeoSelectorModal();
	const [isPending, startTransition] = useTransition();
	const {
		action,
		pending,
		language,
		country: initialCountry,
		geo,
	} = useGeo({
		debug: true,
		useAlternates: true,
	});

	const [lang, setLang] = useState(() => language);
	const [country, setCountry] = useState(() => initialCountry);

	const newGeo = `${lang || language}-${country || initialCountry}` as GeoLocale;

	const startGeoChange = () => {
		if (newGeo === geo) {
			setOpen(false);
			return;
		}

		startTransition(() => {
			action(newGeo);
		});
	};

	return (
		<DrawerRoot open={open} onOpenChange={setOpen}>
			<DrawerViewport>
				<DrawerContent>
					<DrawerMain>
						<div style={{ position: "relative" }}>
							<DrawerClose />
						</div>
						<div className={styles.root}>
							<LanguageForm language={lang} setLanguage={setLang} />
							<hr className={styles.divider} />
							<CountryForm country={country} setCountry={setCountry} />
						</div>
					</DrawerMain>
					<DrawerFooter>
						<Button
							onClick={startGeoChange}
							style={{
								width: "100%",
							}}
							loading={isPending || pending}
						>
							<Translate>Confirm</Translate>
						</Button>
					</DrawerFooter>
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

const UnsuspendedGeoSelectorTrigger = ({
	children,
	asChild,
}: ComponentProps<"button"> & { asChild?: boolean }) => {
	const { setOpen } = useGeoSelectorModal();
	const Component = asChild ? Slot : "button";

	return <Component onClick={() => setOpen(true)}>{children}</Component>;
};

export const GeoSelectorTrigger: typeof UnsuspendedGeoSelectorTrigger = (props) => {
	return (
		<Suspense fallback={props.asChild ? <>{props.children}</> : null}>
			<UnsuspendedGeoSelectorTrigger {...props} />
		</Suspense>
	);
};
